import type { ChangeEvent, KeyboardEvent } from "react"
import { useState } from "react"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"
import { IconSlidersOutline18 as SlidersIcon } from "nucleo-ui-outline-18"

import type { FieldAttributes } from "@productlane/lib/src/tiptap/extensions/mintlify/fields-html"
import {
	fieldAttributesOf,
	fieldKindOf,
	ParamFieldExtension,
	ResponseFieldExtension,
} from "@productlane/lib/src/tiptap/extensions/mintlify/fields"
import {
	FIELD_DATA_TYPE,
	FIELD_LOCATIONS,
	isFieldLocation,
} from "@productlane/lib/src/tiptap/extensions/mintlify/fields-html"
import { IconButton } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"

import type { NodeViewProps } from "@tiptap/react"

const LOCATION_LABEL: Record<(typeof FIELD_LOCATIONS)[number], string> = {
	query: "Query",
	path: "Path",
	body: "Body",
	header: "Header",
}

interface InlineInputProps {
	value: string
	placeholder: string
	label: string
	readOnly: boolean
	onChange: (value: string) => void
	onEnter: () => void
}

// Monospace, so the character count is the width and the pill hugs the text.
function InlineInput({
	value,
	placeholder,
	label,
	readOnly,
	onChange,
	onEnter,
}: InlineInputProps) {
	return (
		<input
			className="pl-field-input"
			value={value}
			placeholder={placeholder}
			aria-label={label}
			readOnly={readOnly}
			spellCheck={false}
			size={Math.max(1, (value || placeholder).length)}
			onChange={(event: ChangeEvent<HTMLInputElement>) =>
				onChange(event.target.value)
			}
			onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
				if (event.key !== "Enter") return
				event.preventDefault()
				onEnter()
			}}
		/>
	)
}

function FieldNodeView({
	node,
	editor,
	getPos,
	updateAttributes,
}: NodeViewProps) {
	const kind = fieldKindOf(node.type.name)
	const attrs = fieldAttributesOf(node.type.name, node.attrs)
	const readOnly = !editor.isEditable
	const [menuOpen, setMenuOpen] = useState(false)

	const update = (patch: Partial<FieldAttributes>) => updateAttributes(patch)

	// Enter in the header moves on to the description, the way a title does.
	const focusBody = () => {
		const pos = getPos()
		if (pos === undefined) return
		editor
			.chain()
			.focus()
			.setTextSelection(pos + 2)
			.run()
	}

	return (
		<NodeViewWrapper
			data-type={FIELD_DATA_TYPE[kind]}
			className={`pl-field pl-${FIELD_DATA_TYPE[kind]}`}
		>
			<div
				className="pl-field-head"
				contentEditable={false}
				suppressContentEditableWarning
			>
				<div className="pl-field-head-row">
					{attrs.pre.map((label, index) => (
						<span key={`pre-${index}`} className="pl-field-pill">
							{label}
						</span>
					))}
					<span className="pl-field-name">
						<InlineInput
							value={attrs.name}
							placeholder="name"
							label="Field name"
							readOnly={readOnly}
							onChange={(name) => update({ name })}
							onEnter={focusBody}
						/>
					</span>
					<span className="pl-field-meta">
						<span className="pl-field-pill">
							<InlineInput
								value={attrs.fieldType}
								placeholder="type"
								label="Field type"
								readOnly={readOnly}
								onChange={(fieldType) => update({ fieldType })}
								onEnter={focusBody}
							/>
						</span>
						{kind === "param" && attrs.location ? (
							<span className="pl-field-pill">{attrs.location}</span>
						) : null}
						{attrs.defaultValue !== null ? (
							<span className="pl-field-pill">
								<span className="pl-field-pill-label">default:</span>
								<InlineInput
									value={attrs.defaultValue}
									placeholder="value"
									label="Default value"
									readOnly={readOnly}
									onChange={(defaultValue) => update({ defaultValue })}
									onEnter={focusBody}
								/>
							</span>
						) : null}
						{attrs.required ? (
							<span className="pl-field-required">required</span>
						) : null}
						{attrs.deprecated ? (
							<span className="pl-field-deprecated">deprecated</span>
						) : null}
						{attrs.post.map((label, index) => (
							<span key={`post-${index}`} className="pl-field-pill">
								{label}
							</span>
						))}
					</span>
				</div>
			</div>
			{readOnly ? null : (
				<div
					className="pl-field-controls"
					contentEditable={false}
					suppressContentEditableWarning
					data-open={menuOpen}
				>
					<DropdownMenu
						open={menuOpen}
						onOpenChange={setMenuOpen}
						className="w-44"
						trigger={
							<IconButton
								variant="ghost"
								size="sm"
								Icon={SlidersIcon}
								aria-label="Field settings"
								className="flex-none cursor-default"
							/>
						}
					>
						{kind === "param" ? (
							<>
								<DropdownMenuLabel>Location</DropdownMenuLabel>
								<DropdownMenuRadioGroup
									value={attrs.location ?? "body"}
									onValueChange={(location) => {
										if (isFieldLocation(location)) update({ location })
									}}
								>
									{FIELD_LOCATIONS.map((location) => (
										<DropdownMenuRadioItem key={location} value={location}>
											{LOCATION_LABEL[location]}
										</DropdownMenuRadioItem>
									))}
								</DropdownMenuRadioGroup>
								<DropdownMenuSeparator />
							</>
						) : null}
						<DropdownMenuCheckboxItem
							checked={attrs.required}
							onCheckedChange={(checked) =>
								update({ required: checked === true })
							}
						>
							Required
						</DropdownMenuCheckboxItem>
						<DropdownMenuCheckboxItem
							checked={attrs.deprecated}
							onCheckedChange={(checked) =>
								update({ deprecated: checked === true })
							}
						>
							Deprecated
						</DropdownMenuCheckboxItem>
						<DropdownMenuCheckboxItem
							checked={attrs.defaultValue !== null}
							onCheckedChange={(checked) =>
								update({ defaultValue: checked === true ? "" : null })
							}
						>
							Default value
						</DropdownMenuCheckboxItem>
					</DropdownMenu>
				</div>
			)}
			<NodeViewContent className="pl-field-body" />
		</NodeViewWrapper>
	)
}

export const ParamFieldWithNodeView = ParamFieldExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(FieldNodeView)
	},
})

export const ResponseFieldWithNodeView = ResponseFieldExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(FieldNodeView)
	},
})

export const FieldsWithNodeView = [
	ParamFieldWithNodeView,
	ResponseFieldWithNodeView,
]
